import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'

const API_URL = process.env.REACT_APP_API_URL

const PaymentTrackerAdd = () => {
  const navigate = useNavigate()

  const [currentUserRole, setCurrentUserRole] = useState('')
  const [suppliers, setSuppliers] = useState([])
  const [filteredSuppliers, setFilteredSuppliers] = useState([])
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('')
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false)
  const [selectedSupplierIndex, setSelectedSupplierIndex] = useState(-1)
  const [isSuppliersLoading, setIsSuppliersLoading] = useState(true)

  const [formData, setFormData] = useState({
    supplierName: '',
    supplierId: '',
    beneficiaryAccountNumber: '',
    beneficiaryIfscNumber: '',
    amount: '',
    debit: '',
    credit: '',
    requestStatus: 'Advance',
    paymentDate: '',
    paymentStatus: 'Pending',
    invoiceStatus: '',
    comments: ''
  })

  const [isLoading, setIsLoading] = useState(false)
  const [isVerifyingIfsc, setIsVerifyingIfsc] = useState(false)
  const [ifscDetails, setIfscDetails] = useState(null)
  const [ifscError, setIfscError] = useState('')
  const [focusedField, setFocusedField] = useState(null)
  const [isIfscFromSupplier, setIsIfscFromSupplier] = useState(false) // Track if IFSC came from supplier data
  const [isAccountFromSupplier, setIsAccountFromSupplier] = useState(false) // Track if account number came from supplier data
  const [userEnteredIfsc, setUserEnteredIfsc] = useState(false) // Track if user manually entered IFSC

  // Fetch active suppliers
  useEffect(() => {
    const fetchSuppliers = async () => {
      setIsSuppliersLoading(true)
      try {
        const payload = {
          action: 'get-active-suppliers'
        }
        const response = await axios.post(API_URL, payload)

        let responseData = response.data
        if (typeof responseData === 'string') {
          try {
            responseData = JSON.parse(responseData)
          } catch (parseError) {
            responseData = response.data
          }
        }

        if (responseData.success === true && responseData.data) {
          setSuppliers(responseData.data)
          setFilteredSuppliers(responseData.data)
        } else {
          toast.error(responseData.message || 'Failed to fetch suppliers', {
            autoClose: 2000
          })
        }
      } catch (error) {
        console.error('Fetch suppliers error:', error)
        toast.error('Failed to load suppliers', { autoClose: 2000 })
      } finally {
        setIsSuppliersLoading(false)
      }
    }

    fetchSuppliers()
  }, [])

  // Get current user role
  useEffect(() => {
    const role = localStorage.getItem('role')
    if (role) {
      setCurrentUserRole(role.toLowerCase())
    }
  }, [])

  // Handle supplier search input
  const handleSupplierSearchChange = (e) => {
    const searchValue = e.target.value
    setSupplierSearchTerm(searchValue)
    setShowSupplierDropdown(true)
    setSelectedSupplierIndex(-1)

    // Filter suppliers based on search term
    if (searchValue.trim() === '') {
      setFilteredSuppliers(suppliers)
    } else {
      const filtered = suppliers.filter(supplier =>
        supplier.clientname.toLowerCase().includes(searchValue.toLowerCase())
      )
      setFilteredSuppliers(filtered)
    }

    // Clear selected supplier if user is typing
    if (formData.supplierId) {
      setFormData(prev => ({
        ...prev,
        supplierName: '',
        supplierId: '',
        beneficiaryAccountNumber: '',
        beneficiaryIfscNumber: ''
      }))
      setIfscDetails(null)
      setIfscError('')
      setIsIfscFromSupplier(false)
      setIsAccountFromSupplier(false)
      setUserEnteredIfsc(false)
    }
  }

  // Handle keyboard navigation in supplier dropdown
  const handleSupplierKeyDown = (e) => {
    if (!showSupplierDropdown || filteredSuppliers.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedSupplierIndex(prev => 
          prev < filteredSuppliers.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedSupplierIndex(prev => prev > 0 ? prev - 1 : -1)
        break
      case 'Enter':
        e.preventDefault()
        if (selectedSupplierIndex >= 0 && selectedSupplierIndex < filteredSuppliers.length) {
          handleSupplierSelect(filteredSuppliers[selectedSupplierIndex])
        } else if (filteredSuppliers.length === 1) {
          // If only one result, select it
          handleSupplierSelect(filteredSuppliers[0])
        }
        break
      case 'Escape':
        setShowSupplierDropdown(false)
        setSelectedSupplierIndex(-1)
        break
      default:
        break
    }
  }

  // Handle supplier selection from dropdown
  const handleSupplierSelect = async (supplier) => {
    setSupplierSearchTerm(supplier.clientname)
    setShowSupplierDropdown(false)
    setSelectedSupplierIndex(-1)

    // Check if supplier has IFSC code and account number
    const hasIfsc = supplier.ifscCode && supplier.ifscCode.trim() !== ''
    const hasAccountNumber = supplier.accountNumber && supplier.accountNumber.trim() !== ''
    
    // Update form data with supplier info
    setFormData(prev => ({
      ...prev,
      supplierName: supplier.clientname,
      supplierId: supplier._id,
      beneficiaryAccountNumber: hasAccountNumber ? supplier.accountNumber : '',
      beneficiaryIfscNumber: hasIfsc ? supplier.ifscCode : ''
    }))

    // Set flags to indicate data came from supplier (make fields read-only)
    setIsIfscFromSupplier(hasIfsc)
    setIsAccountFromSupplier(hasAccountNumber)
    setUserEnteredIfsc(false)

    // If supplier has IFSC code, automatically verify it
    if (hasIfsc && supplier.ifscCode.length === 11) {
      // Clear previous verification results
      setIfscDetails(null)
      setIfscError('')
      setIsVerifyingIfsc(true)

      try {
        // Using public IFSC API
        const response = await axios.get(`https://ifsc.razorpay.com/${supplier.ifscCode}`)
        
        if (response.data) {
          setIfscDetails(response.data)
          toast.success('IFSC code verified successfully', { autoClose: 2000 })
        }
      } catch (error) {
        console.error('IFSC verification error:', error)
        
        if (error.response && error.response.status === 404) {
          setIfscError('Invalid IFSC code. Please check and try again.')
          toast.error('Invalid IFSC code', { autoClose: 2000 })
        } else {
          setIfscError('Unable to verify IFSC code. Please try again.')
          toast.error('Verification failed. Please try again.', { autoClose: 2000 })
        }
      } finally {
        setIsVerifyingIfsc(false)
      }
    }
  }

  const handleAccountNumberChange = (e) => {
    const { name, value } = e.target
    if (value === '' || /^[A-Za-z0-9]+$/.test(value)) {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
  }

  // Handle IFSC field - allow alphanumeric (letters and numbers)
  const handleIfscChange = (e) => {
    const { name, value } = e.target
    // Allow only alphanumeric characters (A-Z, a-z, 0-9)
    if (value === '' || /^[A-Za-z0-9]+$/.test(value)) {
      setFormData(prev => ({ ...prev, [name]: value.toUpperCase() }))
      // Clear previous verification results when IFSC changes
      setIfscDetails(null)
      setIfscError('')
      // Mark that user manually entered IFSC
      setUserEnteredIfsc(true)
    }
  }

  // Verify IFSC code
  const handleVerifyIfsc = async () => {
    const ifscCode = formData.beneficiaryIfscNumber.trim()
    
    if (!ifscCode) {
      toast.error('Please enter IFSC code', { autoClose: 2000 })
      return
    }

    if (ifscCode.length !== 11) {
      toast.error('IFSC code must be 11 characters', { autoClose: 2000 })
      return
    }

    setIsVerifyingIfsc(true)
    setIfscError('')
    setIfscDetails(null)

    try {
      // Using public IFSC API
      const response = await axios.get(`https://ifsc.razorpay.com/${ifscCode}`)
      
      if (response.data) {
        setIfscDetails(response.data)
        toast.success('IFSC code verified successfully', { autoClose: 2000 })
      }
    } catch (error) {
      console.error('IFSC verification error:', error)
      
      if (error.response && error.response.status === 404) {
        setIfscError('Invalid IFSC code. Please check and try again.')
        toast.error('Invalid IFSC code', { autoClose: 2000 })
      } else {
        setIfscError('Unable to verify IFSC code. Please try again.')
        toast.error('Verification failed. Please try again.', { autoClose: 2000 })
      }
    } finally {
      setIsVerifyingIfsc(false)
    }
  }

  // Handle amount field - allow only numbers and one decimal
  const handleAmountChange = (e) => {
    const { name, value } = e.target
    
    // Remove commas for validation
    let cleaned = value.replace(/,/g, '')
    
    // Allow only digits and one decimal point
    cleaned = cleaned.replace(/[^\d.]/g, '')
    
    // Ensure only one decimal point
    const parts = cleaned.split('.')
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('')
    }
    
    setFormData(prev => ({ ...prev, [name]: cleaned }))
  }

  // Get display value with formatting
  const getDisplayValue = (fieldName, value) => {
    // If field is focused, show raw value without formatting
    if (focusedField === fieldName) {
      return value
    }
    
    // If field is amount and has value, format it
    if (fieldName === 'amount' && value) {
      const numValue = parseFloat(value.replace(/,/g, ''))
      if (!isNaN(numValue)) {
        // Format with commas and 2 decimal places
        return numValue.toLocaleString('en-IN', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      }
    }
    
    return value
  }

  // Handle focus
  const handleFocus = (e) => {
    const { name } = e.target
    setFocusedField(name)
  }

  // Handle blur - format the value
  const handleBlur = (e) => {
    const { name, value } = e.target
    setFocusedField(null)
    
    // Format amount field on blur
    if (name === 'amount' && value) {
      const numValue = parseFloat(value.replace(/,/g, ''))
      if (!isNaN(numValue) && numValue > 0) {
        setFormData(prev => ({ ...prev, [name]: numValue.toFixed(2) }))
      }
    }
  }

  // Generic change handler
  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => {
      const next = { ...prev, [name]: value }
      if (name === 'paymentStatus') {
        if (value === 'Paid' && prev.paymentStatus !== 'Paid') {
          next.invoiceStatus = ''
        }
        if (value !== 'Paid') {
          next.invoiceStatus = ''
          next.comments = ''
        }
      }
      return next
    })
  }

  // Simple form validation
  const validateForm = () => {
    if (!formData.supplierName.trim()) {
      toast.error('Supplier name is required', { autoClose: 2000 })
      return false
    }
    if (!formData.supplierId.trim()) {
      toast.error('Please select a valid supplier', { autoClose: 2000 })
      return false
    }
    if (!formData.beneficiaryAccountNumber.trim()) {
      toast.error('Beneficiary account number is required', { autoClose: 2000 })
      return false
    }
    if (!formData.beneficiaryIfscNumber.trim()) {
      toast.error('Beneficiary IFSC number is required', { autoClose: 2000 })
      return false
    }
    if (!formData.amount || isNaN(parseFloat(formData.amount))) {
      toast.error('Valid amount is required', { autoClose: 2000 })
      return false
    }
    // Only validate payment date for admin/supervisor when payment status is "Paid"
    if ((currentUserRole === 'admin' || currentUserRole === 'supervisor') && 
        formData.paymentStatus === 'Paid' && !formData.paymentDate) {
      toast.error('Payment date is required when payment status is Paid', { autoClose: 2000 })
      return false
    }
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    const currentUserId = localStorage.getItem('id')
    if (!currentUserId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      setTimeout(() => {
        navigate('/')
      }, 2000)
      return
    }

    setIsLoading(true)

    try {
      const amount = parseFloat(formData.amount) || 0

      const payload = {
        action: 'create-payment',
        supplierName: formData.supplierName.trim(),
        supplierId: formData.supplierId.trim(),
        beneficiaryAccountNumber: formData.beneficiaryAccountNumber.trim(),
        beneficiaryIfscNumber: formData.beneficiaryIfscNumber.trim(),
        amount: amount,
        debit: formData.debit.trim(),
        credit: formData.credit.trim(),
        requestStatus: formData.requestStatus,
        paymentDate: formData.paymentDate,
        paymentStatus: formData.paymentStatus,
        createdBy: currentUserId
      }

      if (currentUserRole === 'admin' && formData.paymentStatus === 'Paid') {
        if (formData.invoiceStatus) {
          payload.invoiceStatus = formData.invoiceStatus
        }
        if (formData.comments.trim()) {
          payload.comments = formData.comments.trim()
        }
      }

      const response = await axios.post(API_URL, payload)

      let responseData = response.data
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData)
        } catch (parseError) {
          responseData = response.data
        }
      }

      if (responseData.success === true) {
        toast.success(responseData.message || 'Payment created successfully', {
          autoClose: 2000
        })

        // If user manually entered IFSC and it's verified, update supplier in background
        if (userEnteredIfsc && formData.beneficiaryIfscNumber && ifscDetails && formData.supplierId) {
          // Fire and forget - don't wait for response, don't affect frontend
          updateSupplierIfscInBackground(
            formData.supplierId,
            formData.beneficiaryIfscNumber,
            formData.beneficiaryAccountNumber, // Include account number
            ifscDetails,
            currentUserId
          )
        }

        setTimeout(() => {
          navigate('/paymentTracker')
        }, 2000)
      } else {
        toast.error(responseData.message || 'Failed to create payment', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Create payment error:', error)
      if (error.response && error.response.data) {
        let errorData = error.response.data
        if (typeof errorData === 'string') {
          try {
            errorData = JSON.parse(errorData)
          } catch (e) {
            // Use error message as is
          }
        }
        toast.error(errorData.message || 'An error occurred while creating payment', {
          autoClose: 2000
        })
      } else {
        toast.error('Network error. Please check your connection and try again.', {
          autoClose: 2000
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Background function to update supplier IFSC and account number (fire and forget)
  const updateSupplierIfscInBackground = async (supplierId, ifscCode, accountNumber, ifscDetails, currentUserId) => {
    try {
      const updatePayload = {
        action: 'update-supplier',
        id: supplierId,
        currentUserId: currentUserId,
        ifscCode: ifscCode.trim(),
        bankName: ifscDetails.BANK || '',
        branchName: ifscDetails.BRANCH || '',
        bankAddress: ifscDetails.ADDRESS || '',
        bankCity: ifscDetails.CITY || '',
        bankDistrict: ifscDetails.DISTRICT || '',
        bankState: ifscDetails.STATE || ''
      }

      // Add account number if provided
      if (accountNumber && accountNumber.trim()) {
        updatePayload.accountNumber = accountNumber.trim()
      }

      // Fire and forget - don't await, don't handle response
      axios.post(API_URL, updatePayload).catch(error => {
        // Silently log error, don't show to user
        console.log('Background supplier update failed (non-critical):', error)
      })
    } catch (error) {
      // Silently catch any errors
      console.log('Background supplier update error (non-critical):', error)
    }
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">Create Payment</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/paymentTracker">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <form className="form-horizontal" onSubmit={handleSubmit}>
                    <div className="card-body">
                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Supplier Name</label>
                        <div className="col-12 col-md-8">
                          <div style={{ position: 'relative' }}>
                            <input
                              type="text"
                              className="form-control"
                              placeholder={isSuppliersLoading ? "Loading suppliers..." : "Type to search supplier..."}
                              value={supplierSearchTerm}
                              onChange={handleSupplierSearchChange}
                              onKeyDown={handleSupplierKeyDown}
                              onFocus={() => setShowSupplierDropdown(true)}
                              onBlur={() => {
                                // Delay to allow click on dropdown item
                                setTimeout(() => {
                                  setShowSupplierDropdown(false)
                                  setSelectedSupplierIndex(-1)
                                }, 200)
                              }}
                              autoComplete="off"
                              disabled={isSuppliersLoading}
                              required
                            />
                            {isSuppliersLoading && (
                              <div
                                style={{
                                  position: 'absolute',
                                  right: '10px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  pointerEvents: 'none'
                                }}
                              >
                                <i className="fa fa-spinner fa-spin text-muted"></i>
                              </div>
                            )}
                            {showSupplierDropdown && !isSuppliersLoading && filteredSuppliers.length > 0 && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  right: 0,
                                  maxHeight: '200px',
                                  overflowY: 'auto',
                                  backgroundColor: '#fff',
                                  border: '1px solid #ced4da',
                                  borderRadius: '4px',
                                  zIndex: 1000,
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                              >
                                {filteredSuppliers.map((supplier, index) => (
                                  <div
                                    key={supplier._id}
                                    onMouseDown={(e) => {
                                      e.preventDefault()
                                      handleSupplierSelect(supplier)
                                    }}
                                    style={{
                                      padding: '8px 12px',
                                      cursor: 'pointer',
                                      borderBottom: '1px solid #f0f0f0',
                                      backgroundColor: index === selectedSupplierIndex ? '#e9ecef' : '#fff'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#f8f9fa'
                                      setSelectedSupplierIndex(index)
                                    }}
                                    onMouseLeave={(e) => {
                                      if (index !== selectedSupplierIndex) {
                                        e.currentTarget.style.backgroundColor = '#fff'
                                      }
                                    }}
                                  >
                                    {supplier.clientname}
                                  </div>
                                ))}
                              </div>
                            )}
                            {showSupplierDropdown && !isSuppliersLoading && supplierSearchTerm && filteredSuppliers.length === 0 && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  right: 0,
                                  backgroundColor: '#fff',
                                  border: '1px solid #ced4da',
                                  borderRadius: '4px',
                                  padding: '8px 12px',
                                  zIndex: 1000,
                                  color: '#6c757d',
                                  fontSize: '13px'
                                }}
                              >
                                No suppliers found
                              </div>
                            )}
                          </div>
                          {formData.supplierId && (
                            <small className="form-text text-muted">
                              <i className="fa fa-check-circle text-success mr-1"></i>
                              Selected: {formData.supplierName}
                            </small>
                          )}
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Beneficiary Account Number
                        </label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="beneficiaryAccountNumber"
                            className="form-control"
                            placeholder="Account Number"
                            value={formData.beneficiaryAccountNumber}
                            onChange={handleAccountNumberChange}
                            readOnly={isAccountFromSupplier}
                            style={isAccountFromSupplier ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : {}}
                            required
                          />
                          {isAccountFromSupplier && (
                            <small className="form-text text-muted">
                              <i className="fa fa-info-circle mr-1"></i>
                              Auto-populated from supplier data
                            </small>
                          )}
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">
                          Beneficiary IFSC Number
                        </label>
                        <div className="col-12 col-md-8">
                          <div className="input-group">
                            <input
                              type="text"
                              name="beneficiaryIfscNumber"
                              className="form-control"
                              placeholder="IFSC Code (e.g., SBIN0001234)"
                              value={formData.beneficiaryIfscNumber}
                              onChange={handleIfscChange}
                              maxLength="11"
                              required
                              readOnly={isIfscFromSupplier}
                              disabled={isIfscFromSupplier}
                              style={isIfscFromSupplier ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : {}}
                            />
                            <div className="input-group-append">
                              <button
                                type="button"
                                className="btn btn-info"
                                onClick={handleVerifyIfsc}
                                disabled={isVerifyingIfsc || !formData.beneficiaryIfscNumber || formData.beneficiaryIfscNumber.length !== 11 || isIfscFromSupplier}
                              >
                                {isVerifyingIfsc ? (
                                  <>
                                    <i className="fa fa-spinner fa-spin mr-1"></i>
                                    Verifying...
                                  </>
                                ) : (
                                  <>
                                    <i className="fa fa-check-circle mr-1"></i>
                                    Verify
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                          {isIfscFromSupplier && (
                            <small className="form-text text-muted">
                              <i className="fa fa-info-circle mr-1"></i>
                              IFSC code auto-populated from supplier data
                            </small>
                          )}
                          
                          {/* IFSC Verification Results */}
                          {ifscDetails && (
                            <div className="mt-2 p-3" style={{ backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '4px' }}>
                              <div className="d-flex align-items-start">
                                <i className="fa fa-check-circle text-success mr-2" style={{ fontSize: '18px', marginTop: '2px' }}></i>
                                <div style={{ flex: 1 }}>
                                  <p className="mb-1" style={{ fontSize: '13px', fontWeight: '600', color: '#155724' }}>
                                    IFSC Verified Successfully
                                  </p>
                                  <div style={{ fontSize: '12px', color: '#155724' }}>
                                    <p className="mb-1">
                                      <strong>Bank:</strong> {ifscDetails.BANK}
                                    </p>
                                    <p className="mb-1">
                                      <strong>Branch:</strong> {ifscDetails.BRANCH}
                                    </p>
                                    {ifscDetails.ADDRESS && (
                                      <p className="mb-1">
                                        <strong>Address:</strong> {ifscDetails.ADDRESS}
                                      </p>
                                    )}
                                    {ifscDetails.CITY && (
                                      <p className="mb-1">
                                        <strong>City:</strong> {ifscDetails.CITY}
                                      </p>
                                    )}
                                    {ifscDetails.DISTRICT && (
                                      <p className="mb-1">
                                        <strong>District:</strong> {ifscDetails.DISTRICT}
                                      </p>
                                    )}
                                    {ifscDetails.STATE && (
                                      <p className="mb-0">
                                        <strong>State:</strong> {ifscDetails.STATE}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* IFSC Error Message */}
                          {ifscError && (
                            <div className="mt-2 p-2" style={{ backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px' }}>
                              <div className="d-flex align-items-center">
                                <i className="fa fa-exclamation-circle text-danger mr-2"></i>
                                <span style={{ fontSize: '12px', color: '#721c24' }}>{ifscError}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Amount</label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="amount"
                            className="form-control"
                            placeholder="0.00"
                            value={getDisplayValue('amount', formData.amount)}
                            onChange={handleAmountChange}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Payment Reference</label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="debit"
                            className="form-control"
                            placeholder="Payment Reference"
                            value={formData.debit}
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Client Reference</label>
                        <div className="col-12 col-md-8">
                          <input
                            type="text"
                            name="credit"
                            className="form-control"
                            placeholder="Client Reference"
                            value={formData.credit}
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Request Status</label>
                        <div className="col-12 col-md-8">
                          <select
                            name="requestStatus"
                            className="form-control"
                            value={formData.requestStatus}
                            onChange={handleChange}
                          >
                            <option value="Advance">ADVANCE</option>
                            <option value="Credit">CREDIT</option>
                            <option value="Hold">HOLD</option>
                            <option value="Cancelled">CANCELLED</option>
                          </select>
                        </div>
                      </div>

                      {(currentUserRole === 'admin' || currentUserRole === 'supervisor') && (
                        <>
                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">Payment Date</label>
                            <div className="col-12 col-md-8">
                              <input
                                type="date"
                                name="paymentDate"
                                className="form-control"
                                value={formData.paymentDate}
                                onChange={handleChange}
                              />
                            </div>
                          </div>

                          <div className="form-group row">
                            <label className="col-12 col-md-4 col-form-label">Payment Status</label>
                            <div className="col-12 col-md-8">
                              <select
                                name="paymentStatus"
                                className="form-control"
                                value={formData.paymentStatus}
                                onChange={handleChange}
                              >
                                <option value="Pending">Pending</option>
                                <option value="Paid">Paid</option>
                                <option value="Cancelled">Cancelled</option>
                              </select>
                            </div>
                          </div>

                          {currentUserRole === 'admin' && formData.paymentStatus === 'Paid' && (
                            <>
                              <div className="form-group row">
                                <label className="col-12 col-md-4 col-form-label">Invoice Status</label>
                                <div className="col-12 col-md-8">
                                  <select
                                    name="invoiceStatus"
                                    className="form-control"
                                    value={formData.invoiceStatus}
                                    onChange={handleChange}
                                  >
                                    <option value="" disabled>
                                      Select status
                                    </option>
                                    <option value="Pending">Pending</option>
                                    <option value="Accounted">Accounted</option>
                                  </select>
                                </div>
                              </div>

                              <div className="form-group row">
                                <label className="col-12 col-md-4 col-form-label">Comments</label>
                                <div className="col-12 col-md-8">
                                  <textarea
                                    name="comments"
                                    className="form-control"
                                    placeholder="Comments"
                                    value={formData.comments}
                                    onChange={handleChange}
                                    rows={3}
                                  />
                                </div>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>

                    <div className="card-footer text-center">
                      <button
                        type="submit"
                        className="btn btn-blue"
                        disabled={isLoading}
                      >
                        {isLoading ? 'Creating...' : 'Create'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaymentTrackerAdd
